// @flow

import * as React from "react";
import axios from 'axios';
import { isValidEmailAddress, isQuickBooksConnected } from "../Utils"
import qb_connect_button from "../images/qb_connect_button.svg"
import LoadingScreen from "./LoadingScreen.react";
import FrozenSubscriptionBanner from "./FrozenSubscriptionBanner.react";
import {toast} from 'react-toastify';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const swalInstance = withReactContent(Swal);

// A badge followed by its action link/button, vertically centered on one line (wraps on narrow screens).
const settingsRowStyle = {display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "6px"};

class Settings extends React.Component {

  constructor() {
    super();
    this.state = {
      isLoading: true,
      firstName: '',
      lastName: '',
      emailAddress: '',
      quickBooksConnectionStatus: true,
      isFirstNameInvalid: false,
      firstNameErrorMessage: '',
      isLastNameInvalid: false,
      lastNameErrorMessage: '',
      isEmailAddressInvalid: false,
      emailAddressErrorMessage: '',
      isCompanyNameInvalid: false,
      companyNameErrorMessage: '',
      isTaxNumberInvalid: false,
      taxNumberErrorMessage: '',
      currencyType: "",
      isCurrencyTypeInvalid: false,
      currencyTypeErrorMessage: '',
      planName: '',
      taxNumber: '',
      companyName: '',
      billingPeriodEnd: '',
      updateUrl: '',
      updatePaymentButtonLoading: false,
    };

    this.saveSettings = this.saveSettings.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.updatePaymentCard = this.updatePaymentCard.bind(this);
  }

  componentDidMount() {
    Promise.all([
      axios.get(window.SERVER_URL + '/get_settings')
    ])
        .then(([res]) => Promise.all([res.data]))
        .then(([data]) => {
              this.setState({
                isLoading: false,
                firstName: data.result.first_name,
                lastName: data.result.last_name,
                emailAddress: data.result.email_address,
                planName: data.result.plan_name,
                taxNumber: data.result.tax_number,
                companyName: data.result.company_name,
                currencyType: data.result.currency_type,
                billingPeriodEnd: data.result.first_day_next_month,
                updateUrl: data.result.update_url,
                userSettings: data.result,
                quickBooksConnectionStatus: isQuickBooksConnected(data.result),
              });

            }
        );
  }

  // Company-wide fields (name, tax number, currency) are admin-only; the server enforces it too.
  isAdmin() {
    return !!this.state.userSettings && this.state.userSettings.is_admin === true;
  }

  handleChange(evt) {
    let value = evt.target.value;
    if (evt.target.name === "emailAddress") {
      value = value.replace(/[^A-Za-z0-9\@\-\.]/ig, '')
    }

    this.setState({
      [evt.target.name]: value
    });
  }

  reConnectWithQuickBooks() {
    // Same OAuth flow as "Connect a new company": it stores fresh tokens for the company the user picks
    // and makes it the active one.
    window.location.href = window.SERVER_URL + '/oauth/init';
  }

  updatePaymentCard() {
    if (!this.state.updateUrl) {
      return;
    }

    this.setState({updatePaymentButtonLoading: true});
    window.Paddle.Checkout.open({
      override: this.state.updateUrl,
      closeCallback: () => this.setState({updatePaymentButtonLoading: false}),
      successCallback: () => {
        this.setState({updatePaymentButtonLoading: false});
        toast.success("Payment card updated successfully");
      },
    });
  }

  disconnectFromQuickBooks() {
    swalInstance.fire({
      icon: "warning",
      title: "Are you sure you'd like to disconnect the QuickBooks connection?",
      text: "The app will stop working until you'll connect again",
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes, Disconnect QuickBooks Connection",
      confirmButtonColor: "red",
      showCancelButton: true,
      // Keep the pop-up open (spinner on the confirm button, no outside-click/Escape dismissal) until the
      // disconnect request has finished. On failure it stays open with the error, so the user can retry or cancel.
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !swalInstance.isLoading(),
      allowEscapeKey: () => !swalInstance.isLoading(),
      preConfirm: () => axios.post(window.SERVER_URL + '/disconnect_quickbooks', {}, {headers: {"X-Requested-With": "XMLHttpRequest"}})
        .then(() => true)
        .catch(() => {
          swalInstance.showValidationMessage("We couldn't disconnect QuickBooks. Please try again.");
          return false;
        }),
    }).then(function(result) {
      // Only reached with isConfirmed once the disconnect succeeded and the pop-up has closed.
      if (result.isConfirmed) {
        window.location.href = window.SERVER_URL + '/dashboard';
      }
    });
  }

  saveSettings() {
    if (this.state.firstName.length === 0) {
      this.setState({
        isFirstNameInvalid: true,
        firstNameErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (this.state.lastName.length === 0) {
      this.setState({
        isLastNameInvalid: true,
        lastNameErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (this.isAdmin() && this.state.companyName.length === 0) {
      this.setState({
        isCompanyNameInvalid: true,
        companyNameErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (this.isAdmin() && this.state.taxNumber.length === 0) {
      this.setState({
        isTaxNumberInvalid: true,
        taxNumberErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (this.isAdmin() && this.state.currencyType.length === 0) {
      this.setState({
        isCurrencyTypeInvalid: true,
        currencyTypeErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (this.state.emailAddress.length === 0) {
      this.setState({
        isEmailAddressInvalid: true,
        emailAddressErrorMessage: <div>This field cannot be empty</div>
      });
      return;
    }

    if (isValidEmailAddress(this.state.emailAddress) === false) {
      this.setState({
        isEmailAddressInvalid: true,
        emailAddressErrorMessage: <div>Invalid email address</div>
      });
      return;
    }

    this.setState({
      isFirstNameInvalid: false,
      firstNameErrorMessage: '',
      isLastNameInvalid: false,
      lastNameErrorMessage: '',
      isEmailAddressInvalid: false,
      emailAddressErrorMessage: ''
    });

    axios.post(window.SERVER_URL + '/update_user_settings', {
      first_name: this.state.firstName,
      last_name: this.state.lastName,
      company_name: this.state.companyName,
      tax_number: this.state.taxNumber,
      currency_type: this.state.currencyType,
    }).then(function (response) {
      if (response.status === 200) {
        toast.success("Settings saved successfully");
      }
    }).catch(error => {
      toast.error("An error has occurred");
    });

    this.setState({
      isInvalid: false,
      errorMessage: ''
    });
  }

  render() {
    if (this.state.isLoading) {
      return <LoadingScreen />;
    }

    return (
      <div>
        <h2 style={{marginBottom: "24px"}}>Settings</h2>
        {this.state.userSettings && this.state.userSettings.is_active === false ?
          <FrozenSubscriptionBanner userSettings={this.state.userSettings} style={{marginBottom: "24px"}} /> : null}
        <div className="app-grid-row">
          <div className="app-grid-col">
            <div className="app-card">
              <div className="app-card-title">Settings & Personal Details</div>
              {this.isAdmin() ? null : (
                <div style={{color: "#8892a0", fontSize: "0.85rem", marginBottom: "12px"}}>Only a company admin can change the company details.</div>
              )}
              <div className="app-form-group">
                <label className="app-form-label">Company/Seller Name</label>
                <input className={"app-form-input" + (this.state.isCompanyNameInvalid ? " invalid" : "")} name="companyName" disabled={!this.isAdmin()} maxLength={100}
                       onChange={this.handleChange} type="text" value={this.state.companyName} placeholder="Enter Company/Seller Name..." />
                {this.state.companyNameErrorMessage ? <span className="app-form-error">{this.state.companyNameErrorMessage}</span> : null}
              </div>
              <div className="app-form-group">
                <label className="app-form-label">Tax Number</label>
                <input className={"app-form-input" + (this.state.isTaxNumberInvalid ? " invalid" : "")} name="taxNumber" disabled={!this.isAdmin()} maxLength={100}
                       onChange={this.handleChange} type="text" value={this.state.taxNumber} placeholder="Enter Tax Number..." />
                {this.state.taxNumberErrorMessage ? <span className="app-form-error">{this.state.taxNumberErrorMessage}</span> : null}
              </div>
              <div className="app-form-group">
                <label className="app-form-label">Currency Type</label>
                <input className={"app-form-input" + (this.state.isCurrencyTypeInvalid ? " invalid" : "")} name="currencyType" disabled={!this.isAdmin()} maxLength={10}
                       onChange={this.handleChange} type="text" value={this.state.currencyType} placeholder="$" />
                {this.state.currencyTypeErrorMessage ? <span className="app-form-error">{this.state.currencyTypeErrorMessage}</span> : null}
              </div>
              <div className="app-form-group">
                <label className="app-form-label">First Name</label>
                <input className={"app-form-input" + (this.state.isFirstNameInvalid ? " invalid" : "")} name="firstName" maxLength={20}
                       onChange={this.handleChange} type="text" value={this.state.firstName} placeholder="Enter First Name..." />
                {this.state.firstNameErrorMessage ? <span className="app-form-error">{this.state.firstNameErrorMessage}</span> : null}
              </div>
              <div className="app-form-group">
                <label className="app-form-label">Last Name</label>
                <input className={"app-form-input" + (this.state.isLastNameInvalid ? " invalid" : "")} name="lastName" maxLength={20}
                       onChange={this.handleChange} type="text" value={this.state.lastName} placeholder="Enter Last Name..." />
                {this.state.lastNameErrorMessage ? <span className="app-form-error">{this.state.lastNameErrorMessage}</span> : null}
              </div>
              <div className="app-form-group">
                <label className="app-form-label">Email Address</label>
                <input className={"app-form-input" + (this.state.isEmailAddressInvalid ? " invalid" : "")} name="emailAddress" disabled maxLength={255}
                       onChange={this.handleChange} type="email" value={this.state.emailAddress} placeholder="Enter Email Address..." />
                {this.state.emailAddressErrorMessage ? <span className="app-form-error">{this.state.emailAddressErrorMessage}</span> : null}
              </div>
              <div style={{textAlign: "right"}}>
                <button className="btn btn-primary" onClick={this.saveSettings}>Save</button>
              </div>
            </div>
          </div>
          <div className="app-grid-col">
            <div className="app-card">
              <div className="app-card-title">Plan & QuickBooks Connection</div>
              <div className="app-form-group">
                <label className="app-form-label">QuickBooks Connection Status</label>
                <div style={settingsRowStyle}>
                  <span className={"app-badge " + (this.state.quickBooksConnectionStatus ? "app-badge-success" : "app-badge-danger")}>
                    {this.state.quickBooksConnectionStatus ? "Connected To QuickBooks" : "Not Connected To QuickBooks"}
                  </span>
                  {this.state.quickBooksConnectionStatus ? <a style={{fontSize: "12px", color: "#c92a2a"}} href="javascript:void(0)" onClick={this.disconnectFromQuickBooks}>Disconnect from QuickBooks</a>
                    : <a href="javascript:void(0)" onClick={this.reConnectWithQuickBooks} style={{display: "inline-flex"}}><img src={qb_connect_button} alt="Connect to QuickBooks" style={{display: "block"}} /></a>}
                </div>
              </div>
              <div className="app-form-group">
                <label className="app-form-label">Subscription Plan</label>
                <div style={settingsRowStyle}>
                  <span className="app-badge app-badge-success">{this.state.planName}</span>
                  <a style={{fontSize: "12px"}} href="javascript:void(0)" onClick={() => this.props.history.push('/subscriptions')}>Change Plan</a>
                </div>
                {this.state.billingPeriodEnd ?
                  <div style={{color: "#8892a0", fontSize: "0.85rem", marginTop: "6px"}}>Billing period ends {this.state.billingPeriodEnd}</div>
                : null}
              </div>
              {this.state.updateUrl ?
                <div className="app-form-group">
                  <label className="app-form-label">Payment Method</label>
                  <div style={settingsRowStyle}>
                    {this.state.updatePaymentButtonLoading
                      ? <span style={{fontSize: "12px", color: "#8892a0"}}>Please wait…</span>
                      : <a style={{fontSize: "12px"}} href="javascript:void(0)" onClick={this.updatePaymentCard}>Update Payment Card</a>}
                  </div>
                </div>
              : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Settings;